import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import Layout from '../../components/Layout';
import FormFieldText from '../../components/FormComponents/FormFieldText';
import FormFieldSwitch from '../../components/FormComponents/FormFieldSwitch';
import { useMst } from '../../model/Root';
import {
  createYoutubePlaylist,
  getYoutubePlaylist,
  getYoutubePlaylists,
  updateYoutubePlaylist,
} from '../../services/youtubePlaylistsApi';
import type { YoutubePlaylist } from '../../types';

type YoutubePlaylistFormValues = {
  id?: number;
  playlistInput: string; // URL or raw playlistId
  playlistId: string; // read-only after backend normalizes
  playlistUrl: string | null;
  displayName: string | null;
  churchId: string | null;
  enabled: boolean;
  sortOrder: number | string;
  createdAt?: string;
  updatedAt?: string;
};

const DEFAULT_VALUES: YoutubePlaylistFormValues = {
  playlistInput: '',
  playlistId: '',
  playlistUrl: null,
  displayName: null,
  churchId: null,
  enabled: true,
  sortOrder: 0,
  createdAt: undefined,
  updatedAt: undefined,
};

const CreateSchema = Yup.object().shape({
  playlistInput: Yup.string().trim().max(1024).required('Required'),
  displayName: Yup.string().max(256).nullable(),
  enabled: Yup.boolean().required(),
  churchId: Yup.string().nullable(),
});

const looksLikeUrl = (value: string) => /^https?:\/\//i.test(value.trim());

const EditSchema = Yup.object().shape({
  playlistInput: Yup.string().trim().max(1024).required('Required'),
  displayName: Yup.string().max(256).nullable(),
  enabled: Yup.boolean().required(),
  churchId: Yup.string().nullable(),
});

const buildUpsertPayload = (values: YoutubePlaylistFormValues) => {
  const input = values.playlistInput.trim();
  const trimmedName = (values.displayName ?? '').trim();

  return {
    ...(looksLikeUrl(input) ? { playlistUrl: input } : { playlistId: input }),
    displayName: trimmedName.length > 0 ? trimmedName : null,
    enabled: !!values.enabled,
    churchId: values.churchId && values.churchId.trim().length > 0 ? values.churchId : null,
  };
};

const buildUpdatePayload = (values: YoutubePlaylistFormValues) => {
  const input = values.playlistInput.trim();
  const trimmedName = (values.displayName ?? '').trim();

  return {
    ...(looksLikeUrl(input) ? { playlistUrl: input } : { playlistId: input }),
    displayName: trimmedName.length > 0 ? trimmedName : null,
    enabled: !!values.enabled,
    churchId: values.churchId && values.churchId.trim().length > 0 ? values.churchId : null,
  };
};

const YoutubePlaylistEditScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isCreate = !id || id === 'create';
  const {
    churchStore: { churches, fetchChurches },
    errorStore: { setError },
  } = useMst();

  const [initialValues, setInitialValues] = useState<YoutubePlaylistFormValues>(DEFAULT_VALUES);
  const [loading, setLoading] = useState(false);
  const [fetchedOnce, setFetchedOnce] = useState(false);

  useEffect(() => {
    if (churches.length === 0) {
      void fetchChurches('', true);
    }
  }, [churches.length, fetchChurches]);

  useEffect(() => {
    let mounted = true;
    if (!isCreate && id) {
      setLoading(true);
      (async () => {
        try {
          const data = await getYoutubePlaylist(id);
          if (!mounted) return;
          setInitialValues({
            id: data.id,
            playlistInput: data.playlistUrl ?? data.playlistId ?? '',
            playlistId: data.playlistId ?? '',
            playlistUrl: data.playlistUrl ?? null,
            displayName: data.displayName ?? null,
            churchId: data.churchId ?? null,
            enabled: !!data.enabled,
            sortOrder: Number.isFinite(data.sortOrder) ? data.sortOrder : 0,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          });
        } catch (err) {
          console.error(err);
          setError('general_error_message');
        } finally {
          if (mounted) {
            setLoading(false);
            setFetchedOnce(true);
          }
        }
      })();
    } else {
      setInitialValues(DEFAULT_VALUES);
      setFetchedOnce(true);
    }
    return () => {
      mounted = false;
    };
  }, [id, isCreate, setError]);

  const titleLabel = useMemo(
    () =>
      isCreate
        ? (t('youtubePlaylists.create') ?? 'Add YouTube playlist')
        : (t('youtubePlaylists.edit') ?? 'Edit YouTube playlist'),
    [isCreate, t]
  );

  const reorderNewPlaylistToTop = useCallback(
    async (createdId: number) => {
      try {
        const all = await getYoutubePlaylists();
        const list = Array.isArray(all) ? all : [];
        if (list.length === 0) return;

        const created = list.find((p) => p.id === createdId);
        if (!created) return;

        // Current order is based on sortOrder asc.
        const sorted = list.slice().sort((a, b) => {
          const aOrder = Number.isFinite(a.sortOrder) ? a.sortOrder : 0;
          const bOrder = Number.isFinite(b.sortOrder) ? b.sortOrder : 0;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return (a.displayName ?? a.playlistId).localeCompare(b.displayName ?? b.playlistId);
        });

        const next = [created, ...sorted.filter((p) => p.id !== createdId)];
        const updates = next
          .map((p, index) => {
            if (!p.id) return null;
            const prevSort = p.sortOrder ?? 0;
            if (Number(prevSort) === index) return null;
            return { id: p.id, sortOrder: index };
          })
          .filter(Boolean) as Array<{ id: number; sortOrder: number }>;

        if (updates.length === 0) return;
        await Promise.all(updates.map((u) => updateYoutubePlaylist(u.id, { sortOrder: u.sortOrder })));
      } catch (err) {
        console.error(err);
        // Don't block create UX; show generic error and keep going.
        setError('general_error_message');
      }
    },
    [setError]
  );

  const onSubmit = async (values: YoutubePlaylistFormValues, helpers: FormikHelpers<YoutubePlaylistFormValues>) => {
    const payload = isCreate ? buildUpsertPayload(values) : buildUpdatePayload(values);
    try {
      let saved: YoutubePlaylist | undefined;
      if (isCreate) {
        saved = await createYoutubePlaylist(payload);
      } else if (id) {
        saved = await updateYoutubePlaylist(id, payload);
      }
      helpers.setSubmitting(false);
      if (saved?.id) {
        if (isCreate) {
          // User expectation: new entries appear at the top of the list.
          await reorderNewPlaylistToTop(saved.id);
          navigate('/programs/youtube-playlists');
          return;
        }
        navigate(`/programs/youtube-playlists/${saved.id}`);
        return;
      }
      navigate('/programs/youtube-playlists');
    } catch (err) {
      console.error(err);
      setError('general_error_message');
      helpers.setSubmitting(false);
    }
  };

  return (
    <Layout title={titleLabel}>
      {!isCreate && loading && !fetchedOnce ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">{t('loading') ?? 'Loading'}</Typography>
        </Box>
      ) : (
        <Formik
          key={initialValues.id ?? 'new'}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={isCreate ? CreateSchema : EditSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, isValid, values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2}>
                {isCreate ? (
                  <Grid item xs={12}>
                    <FormFieldText
                      name="playlistInput"
                      label={t('youtubePlaylists.playlistInput') ?? 'Playlist URL or ID'}
                      inputTagProps={{ maxLength: 1024 }}
                      autoFocus
                      helperText={t('youtubePlaylists.playlistInputHelp') ?? 'Paste a full playlist URL or a PL... id.'}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {t('youtubePlaylists.detailsTitle') ?? 'Details'}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t('youtubePlaylists.recordId') ?? 'Record ID'}
                          </Typography>
                          <Typography variant="body1">{values.id ?? '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t('youtubePlaylists.playlistId') ?? 'Playlist ID'}
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {values.playlistId || '-'}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t('youtubePlaylists.playlistUrl') ?? 'Playlist URL'}
                          </Typography>
                          {values.playlistUrl ? (
                            <Box component="a" href={values.playlistUrl} target="_blank" rel="noreferrer">
                              {t('youtubePlaylists.open') ?? 'Open'}
                            </Box>
                          ) : (
                            <Typography variant="body1">-</Typography>
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t('youtubePlaylists.createdAt') ?? 'Created'}
                          </Typography>
                          <Typography variant="body1">
                            {values.createdAt ? new Date(values.createdAt).toLocaleString() : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t('youtubePlaylists.updatedAt') ?? 'Updated'}
                          </Typography>
                          <Typography variant="body1">
                            {values.updatedAt ? new Date(values.updatedAt).toLocaleString() : '-'}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {t('youtubePlaylists.editHelp') ??
                          'You can change the playlist URL/ID below. Backend will normalize and update the PL... id.'}
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {!isCreate && (
                  <Grid item xs={12}>
                    <FormFieldText
                      name="playlistInput"
                      label={t('youtubePlaylists.playlistInput') ?? 'Playlist URL or ID'}
                      inputTagProps={{ maxLength: 1024 }}
                      helperText={
                        t('youtubePlaylists.playlistInputEditHelp') ??
                        t('youtubePlaylists.playlistInputHelp') ??
                        'Paste a full playlist URL or a PL... id.'
                      }
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <FormFieldText
                    name="displayName"
                    label={t('youtubePlaylists.displayName') ?? 'Display name'}
                    inputTagProps={{ maxLength: 256 }}
                    helperText={t('youtubePlaylists.displayNameHelp') ?? 'Optional label shown in the app.'}
                  />
                </Grid>
                <Grid item xs={12} md={3} display="flex" alignItems="center">
                  <FormFieldSwitch name="enabled" label={t('enabled') ?? 'Enabled'} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="youtube-playlist-church-select">{t('church.title') ?? 'Church'}</InputLabel>
                    <Select
                      labelId="youtube-playlist-church-select"
                      label={t('church.title') ?? 'Church'}
                      value={values.churchId ?? 'null'}
                      onChange={(e) => setFieldValue('churchId', e.target.value === 'null' ? null : e.target.value)}
                    >
                      <MenuItem value="null">{t('youtubePlaylists.noChurch') ?? 'No church'}</MenuItem>
                      {churches.map((c, index) => (
                        <MenuItem key={c.id ?? `${c.name ?? 'church'}-${index}`} value={c.id ?? ''}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {!isCreate && (
                  <Grid item xs={12} md={6}>
                    <FormFieldText
                      name="playlistId"
                      label={t('youtubePlaylists.playlistId') ?? 'Playlist ID'}
                      readonly
                      helperText={
                        t('youtubePlaylists.playlistIdHelp') ?? 'Backend stores a normalized PL... id after save.'
                      }
                    />
                  </Grid>
                )}

                <Grid item xs={12} display="flex" gap={1}>
                  <Button type="submit" variant="contained" disabled={isSubmitting || !isValid}>
                    {t('save') ?? 'Save'}
                  </Button>
                  <Button variant="outlined" onClick={() => navigate('/programs/youtube-playlists')}>
                    {t('cancel') ?? 'Cancel'}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </Layout>
  );
});

export default YoutubePlaylistEditScreen;
